import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit } from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { map, combineLatest, Subscription, distinctUntilChanged } from 'rxjs';
import { DEFAULT_SNACKBAR_CONFIG, Logger, PreviewMode } from 'src/app/@shared';
import { Product, ProductDomain, OfferProductService, MontageBuilderService, AssetsListService } from '../..';
import { MatTableDataSource } from '@angular/material/table';

const log = new Logger('OfferProductsTableComponent');

@Component({
  selector: 'app-offer-products-table',
  templateUrl: './offer-products-table.component.html',
  styleUrls: ['./offer-products-table.component.scss']
})

export class OfferProductsTableComponent<T extends Product> implements OnInit {
  viewModel$ = combineLatest([
    this.offerProductService.products$,
    this.offerProductService.isLoading$,
    this.offerProductService.totalRecords$,
    this.offerProductService.page$,
  ]).pipe(
    map(([products, isLoading, totalRecords, page]) => {
      if (products.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.offerProductService.page(page);
        this.offerProductService.reload();
        const preselectedProducts = products.filter(product => product.IsFeaturedProduct);
        this.montageBuilderService.preSelectedProductsBehaviourSubject.next(preselectedProducts);
      }
      return { products, isLoading, totalRecords, page }
    }),
  );

  PreviewMode = PreviewMode;
  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'Montage', 'ProductName', 'ProductCategoryName', 'ProductBrandName', 'ClientKey', 'Rank', 'Actions'];
  productsList: MatTableDataSource<ProductDomain<T>> = new MatTableDataSource();
  selection = new SelectionModel<ProductDomain<T>>(true, [], true, (o1, o2) => o1.DomainId === o2.DomainId);
  selectedProducts: any[] = [];
  constructor(private offerProductService: OfferProductService<T>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private matSnackBar: MatSnackBar,
    private mediaObserver: MediaObserver,
    private montageBuilderService: MontageBuilderService,
    private assetsListService: AssetsListService) { }

  ngOnInit(): void {
    log.debug('init');
    this.offerProductService.offerId = this.route.snapshot.params['offerId'];
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Montage', 'ProductName'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['ProductName', 'Image', 'Montage', 'ProductCategoryName', 'ClientKey', 'Rank', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'Image', 'Montage', 'ProductName', 'ProductCategoryName', 'ProductBrandName', 'ClientKey', 'Rank', 'Actions'];
        }
      });

    this.offerProductService.products$.subscribe((data) => {
      this.productsList = new MatTableDataSource(data);
    });
    this.selection.changed.subscribe(() => {
      this.montageBuilderService.selectedOfferProductsBehaviorSubject.next(this.selection.selected);
    });
  }

  ngAfterViewInit(): void {
    const selectedProducts = this.montageBuilderService.selectedOfferProductsBehaviorSubject.value;
    if (selectedProducts && selectedProducts.length > 0) {
      this.selection.select(...selectedProducts);
    }
  }


  onSort(sortState: Sort): void {
    this.offerProductService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.offerProductService.page(pageEvent);
  }

  onRowDoubleClick(id: string) {
    this.router.navigate([`${id}/details`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.productsList.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.productsList.data.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }
  getSelected(selectedProduct: any) {
    this.selectedProducts.push(selectedProduct);
  }

  onFeaturedChange(checked: boolean, product: ProductDomain<T>) {
    this.assetsListService.updateIsFeaturedProduct('OFFER', this.route.snapshot.params['offerId'], product.DomainId, checked).subscribe({
      next: () => {
        this.matSnackBar.open(`${product.Detail.ProductName} featured status updated`, 'OK', DEFAULT_SNACKBAR_CONFIG);
      },
      error: (error) => {
        console.error('Error updating product featured status', error);
      }
    });
  }

}
